import {ReactElement} from "react";
import {BracketMatch as BracketMatchType, CompetitionMatchStatus, TournamentTeam} from "@/competitions/tournaments/types";
import "./BracketMatch.scss";
import {useGetMatchLobby, useMatchLiveScore} from "@/competitions/tournaments/hooks";

/** Only allow http/https URLs in img src to prevent injection */
const isSafeUrl = (url: string): boolean => {
    try {
        const parsed = new URL(url, window.location.origin);
        return parsed.protocol === "http:" || parsed.protocol === "https:";
    } catch {
        return false;
    }
};

interface BracketMatchProps {
    tournamentId: string;
    match: BracketMatchType;
    /** Full team data for seeds, logos, etc. */
    teams: Record<string, TournamentTeam>;
    /** Legacy support - team ID to name mapping */
    teamNames?: Record<string, string>;
    onMatchClick?: (matchId: string) => void;
    /** The viewer's own team id — gets the cyan YOU tag + accent. */
    highlightTeamId?: string;
    /** Short match label (A, B, …) shown top-left, like the mockup. */
    label?: string;
    /** Display options */
    showSeeds?: boolean;
    showLogos?: boolean;
    showTime?: boolean;
    compact?: boolean;
}

/** Deterministic gradient for a team logo placeholder, from the team id. */
function logoGradient(seed: string): string {
    let h = 0;
    for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) % 360;
    return `linear-gradient(135deg, hsl(${h} 40% 22%), hsl(${(h + 40) % 360} 65% 45%))`;
}

/** Running totals are sums of per-event points — usually integers, but a fractional pointsPerEvent is legal. */
const formatLiveScore = (value: number): string =>
    Number.isInteger(value) ? String(value) : value.toFixed(1);

/**
 * Individual match in a bracket
 * Shows two teams with seeds, logos, scores, and winner highlight
 */
export default function BracketMatch({
    tournamentId,
    match,
    teams,
    teamNames,
    onMatchClick,
    highlightTeamId,
    label,
    showSeeds = true,
    showLogos = true,
    showTime = false,
    compact = false,
}: BracketMatchProps): ReactElement {
    // Get team data - prefer full team object, fallback to teamNames
    const team1 = match.team1Id ? teams[match.team1Id] : undefined;
    const team2 = match.team2Id ? teams[match.team2Id] : undefined;

    const team1Name = team1?.name || match.team1Id && teamNames?.[match.team1Id] || "TBD";
    const team2Name = team2?.name || match.team2Id && teamNames?.[match.team2Id] || "TBD";

    const isCompleted = match.status === CompetitionMatchStatus.COMPLETED;
    const isLive = match.status === CompetitionMatchStatus.IN_PROGRESS;
    const isDisputed = match.status === CompetitionMatchStatus.DISPUTED;
    const isForfeit = match.status === CompetitionMatchStatus.FORFEIT;
    const isAwaitingConfirmation = match.status === CompetitionMatchStatus.AWAITING_CONFIRMATION;
    const decided = isCompleted && !!match.winnerId;
    const team1Won = decided && match.winnerId === match.team1Id;
    const team2Won = decided && match.winnerId === match.team2Id;
    // Only dim the loser once a winner exists (never dim a TBD/pending row).
    const team1Lost = decided && !!match.team1Id && !team1Won;
    const team2Lost = decided && !!match.team2Id && !team2Won;
    const team1You = !!highlightTeamId && match.team1Id === highlightTeamId;
    const team2You = !!highlightTeamId && match.team2Id === highlightTeamId;
    const isYou = team1You || team2You;

    // Running auto-score: socket frames patch lobbyData.liveScore between polls
    useMatchLiveScore(tournamentId, match.matchId);

    /*
     * Poll the lobby ONLY while the match is live. Every bracket card mounts
     * this hook, and a 32-team bracket is 31 cards — ungated, that was 31
     * lobby fetches every 20s per viewer (4+ DB reads each) for data that
     * only renders on the one or two cards actually in progress.
     */
    const {data: lobbyData} = useGetMatchLobby(tournamentId, match.matchId, isLive);

    /*
     * Running auto-score, IN_PROGRESS only. The accumulation hash behind it
     * is deleted on pre-fill, so once the match moves on the board must show
     * the standing/final score (participant.score) and nothing else. A visible
     * 0 – 0 from the moment the match opens is deliberate: it tells players
     * scoring is armed, so a dead mapper reads as a tally that never moves.
     */
    const liveScore = match.status === CompetitionMatchStatus.IN_PROGRESS ? lobbyData?.liveScore : undefined;
    const p1Display = liveScore ? formatLiveScore(liveScore.team1Score) : match.team1Score ?? "–";
    const p2Display = liveScore ? formatLiveScore(liveScore.team2Score) : match.team2Score ?? "–";

    const teamClass = (won: boolean, lost: boolean, tbd: boolean) =>
        `bracket-match__team${won ? " bracket-match__team--winner" : ""}` +
        `${lost ? " bracket-match__team--loser" : ""}${tbd ? " bracket-match__team--tbd" : ""}`;

    // Format scheduled time
    const formatTime = (timestamp?: number) => {
        if (!timestamp) return null;
        const date = new Date(timestamp);
        return date.toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    };

    // BYE match - single team advances
    if (match.isBye) {
        return (
            <div className={`bracket-match bracket-match--bye ${compact ? "bracket-match--compact" : ""}`}>
                <div className="bracket-match__team bracket-match__team--winner">
                    {showSeeds && <span className="bracket-match__seed">{team1?.seed || "-"}</span>}
                    {showLogos && team1?.logoUrl && isSafeUrl(team1.logoUrl) &&
                        <img src={team1.logoUrl} alt="" className="bracket-match__logo" />
                    }
                    <span className="bracket-match__name">{team1Name}</span>
                    <span className="bracket-match__score">BYE</span>
                </div>
            </div>
        );
    }

    const statusClass = match.status.toLowerCase().replace(/_/g, "-");

    const matchContent =
        <>
            {label && <span className="bracket-match__label">{label}</span>}

            {/* Match header with round info and time */}
            {showTime && match.scheduledTime &&
                <div className="bracket-match__header">
                    <span className="bracket-match__time">{formatTime(match.scheduledTime)}</span>
                </div>
            }

            {/* Teams */}
            <div className="bracket-match__teams">
                {/* Team 1 */}
                <div className={teamClass(team1Won, team1Lost, !match.team1Id)}>
                    {showSeeds && <span className="bracket-match__seed">{team1?.seed ?? "-"}</span>}
                    {showLogos &&
                        (team1?.logoUrl && isSafeUrl(team1.logoUrl)
                            ? <img src={team1.logoUrl} alt="" className="bracket-match__logo" />
                            : <span className="bracket-match__logo" style={match.team1Id ? {background: logoGradient(match.team1Id)} : undefined}>
                                {team1 ? (team1.tag || team1.name).slice(0, 2).toUpperCase() : ""}
                            </span>)
                    }
                    <span className="bracket-match__name">{team1Name}</span>
                    {team1You && <span className="bracket-match__you">YOU</span>}
                    <span className="bracket-match__score">{p1Display}</span>
                </div>

                {/* Team 2 */}
                <div className={teamClass(team2Won, team2Lost, !match.team2Id)}>
                    {showSeeds && <span className="bracket-match__seed">{team2?.seed ?? "-"}</span>}
                    {showLogos &&
                        (team2?.logoUrl && isSafeUrl(team2.logoUrl)
                            ? <img src={team2.logoUrl} alt="" className="bracket-match__logo" />
                            : <span className="bracket-match__logo" style={match.team2Id ? {background: logoGradient(match.team2Id)} : undefined}>
                                {team2 ? (team2.tag || team2.name).slice(0, 2).toUpperCase() : ""}
                            </span>)
                    }
                    <span className="bracket-match__name">{team2Name}</span>
                    {team2You && <span className="bracket-match__you">YOU</span>}
                    <span className="bracket-match__score">{p2Display}</span>
                </div>
            </div>

            {/* Live map/score footer strip (mockup's .live-map). */}
            {isLive && (match.team1Score != null || match.team2Score != null) &&
                <div className="bracket-match__live-map">
                    Live · {match.team1Score ?? 0}–{match.team2Score ?? 0}
                </div>
            }

            {/* Reported but unconfirmed — the score shown is pending, not final */}
            {isAwaitingConfirmation &&
                <div className="bracket-match__status-badge bracket-match__status-badge--awaiting">
                    AWAITING CONFIRMATION
                </div>
            }

            {/* Disputed badge */}
            {isDisputed &&
                <div className="bracket-match__status-badge bracket-match__status-badge--disputed">
                    DISPUTED
                </div>
            }

            {/* Forfeit badge */}
            {isForfeit &&
                <div className="bracket-match__status-badge bracket-match__status-badge--forfeit">
                    FORFEIT
                </div>
            }
        </>;
    const className = `bracket-match bracket-match--${statusClass}${isLive ? " bracket-match--live" : ""}` +
        `${isYou ? " bracket-match--you" : ""}${compact ? " bracket-match--compact" : ""}` +
        `${onMatchClick ? " bracket-match--clickable" : ""}`;

    if (onMatchClick) {
        return (
            <button
                type="button"
                className={className}
                onClick={() => onMatchClick(match.matchId)}
            >
                {matchContent}
            </button>
        );
    }

    return (
        <div className={className}>
            {matchContent}
        </div>
    );
}
